import EmbedController from "../../embed/EmbedController";
import { isHostedHttpUrl } from "./isHostedHttpUrl";

/**
 * Unique Shopify product for one configuration.
 * Iframe builds the body. Parent tt_embed.js POSTs it to the builder origin
 * (window.location.origin /.netlify/functions/...). Admin token stays on Netlify.
 * Response shapes match Thrill Seekers: { product } and { image }.
 *
 * A hosted image URL is sent on the product as images[].src.
 * A canvas data URL is not. upload-shopify-image needs the new product id,
 * then its image.src is the URL the cart stores.
 *
 * variants[0].price is the MSRP price, not the discounted price.
 * create-shopify-product recomputes that MSRP price and ignores a posted
 * dollar amount. That function also replaces title. body_html stays as built here.
 */

function escapeHtml(value) {
  return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function productBodyEntries(properties) {
  const visible = [];
  const hidden = [];
  let configurationUrl = null;
  Object.entries(properties || {}).forEach((entry) => {
    const [key] = entry;
    if (key === "_customURL") configurationUrl = entry;
    else if (key.startsWith("_")) hidden.push(entry);
    else visible.push(entry);
  });
  if (configurationUrl) hidden.push(configurationUrl);
  return visible.concat(hidden);
}

function generateProductHtml(properties) {
  let html = "";
  productBodyEntries(properties).forEach(([key, value]) => {
    if (!key || value == null || value === "") return;
    if (typeof value === "string" && value.indexOf("data:") === 0) return;
    html += `<p><b>${escapeHtml(key)}:</b> ${escapeHtml(value)}</p>`;
  });
  return html;
}

function isDataUrl(value) {
  return typeof value === "string" && value.startsWith("data:");
}

// Same cart lines getFinalPrice reads. Drop image bytes; they are not prices.
function cartItemForPrice(item) {
  if (!item || typeof item !== "object") return item;
  const next = { ...item };
  delete next.uploadedLogoBase64;
  if (isDataUrl(next.previewImg)) next.previewImg = "";
  if (isDataUrl(next.uploadedLogoSrc)) delete next.uploadedLogoSrc;
  return next;
}

export function generateProductBody(productObj, properties, finalPrice, imageUrl, cartData) {
  const titleName = productObj?.displayName || productObj?.part_number || "Configuration";
  const product = {
    title: `Custom Caliper Covers: ${titleName}`,
    body_html: generateProductHtml(properties),
    vendor: "MGP Caliper Covers",
    product_type: "tastetest3d-order",
    template_suffix: "3dcustomized",
    tags: ["tastetest3d-order"],
    variants: [
      {
        price: finalPrice,
        inventory_management: null,
      },
    ],
  };
  // Shopify fetches src. A data URL has to go through the image upload after this product exists.
  if (isHostedHttpUrl(imageUrl)) {
    product.images = [{ src: imageUrl }];
  }
  return {
    product,
    productsState: { activeObj: productObj || {} },
    cartData: (Array.isArray(cartData) ? cartData : []).map(cartItemForPrice),
  };
}

export function imageUrlFromShopifyImage(image) {
  const record = image && image.image ? image.image : image;
  const url = record && (record.src || record.url);
  return isHostedHttpUrl(url) ? url : "";
}

export function productImageFields(productId, imageSrc) {
  const filename = `${dateStamp()}-${productId}.png`;
  if (isHostedHttpUrl(imageSrc)) return { src: imageSrc, filename };
  return { attachment: stripDataUrl(imageSrc), filename };
}

function stripDataUrl(src) {
  if (typeof src !== "string") return src;
  const marker = "base64,";
  const index = src.indexOf(marker);
  return index === -1 ? src : src.slice(index + marker.length);
}

function dateStamp() {
  const current = new Date();
  return `${current.getDate()}-${current.getMonth() + 1}-${current.getFullYear()}`;
}

function netlifyFunctionUrl(name) {
  return `${window.location.origin}/.netlify/functions/${name}`;
}

async function createProductImage(productId, imageSrc) {
  const url = netlifyFunctionUrl("upload-shopify-image");
  const body = {
    product_id: productId,
    image: productImageFields(productId, imageSrc),
  };
  let image;
  try {
    image = await EmbedController.sendUploadProductImage(url, body);
  } catch (err) {
    image = await EmbedController.sendUploadProductImage(url, body);
  }
  const imageUrl = imageUrlFromShopifyImage(image);
  if (!imageUrl) throw new Error("Shopify image upload did not return an image URL");
  return imageUrl;
}

export async function createUniqueShopifyProduct({ productObj, properties, finalPrice, customImageBase64, cartData }) {
  const hostedImageUrl = isHostedHttpUrl(customImageBase64) ? customImageBase64 : "";
  const productBody = generateProductBody(productObj, properties, finalPrice, hostedImageUrl, cartData);
  const response = await EmbedController.sendShopifyProduct(netlifyFunctionUrl("create-shopify-product"), productBody);
  const product = response?.product || response;
  const variantId = product?.variants?.[0]?.id;
  if (!product?.id || !variantId) {
    throw new Error("Shopify product create did not return a variant id");
  }

  let imageUrl = imageUrlFromShopifyImage(product?.images?.[0]) || hostedImageUrl;
  if (!imageUrl && customImageBase64) {
    imageUrl = await createProductImage(product.id, customImageBase64);
  }
  return {
    productId: product.id,
    variantId,
    handle: product.handle,
    imageUrl,
  };
}
